import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["IconComponent", "clearable", "onChange", "boxProps", "width", "theme", "clearButtonProps"],
    _excluded2 = ["theme", "color", "size", "zIndex"],
    _excluded3 = ["children", "accept", "capture", "files", "multiple", "required", "value", "disabled", "inputProps", "onChange"],
    _excluded4 = ["value", "children", "clearable", "selectedValuePlaceholder", "placeholder", "optionRenderer", "onChange"],
    _excluded5 = ["name", "id", "dropdownProps", "noResultsLabel", "menuListProps", "typeahead", "onFilterChange", "filterEnabled", "FooterLink", "footerLinkVariant"],
    _excluded6 = ["width", "height", "sliderSpacing", "transitionDuration", "containerProps", "className"];

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import styled, { withTheme } from 'styled-components';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Box from '../Box';
import T from '../Text';
import { focusBorder, focusBoxShadow, focusOutline, isFunction, mergeRefs, truncate, useThemeDefaults } from "../utils";
import { focusFieldOutline, focusFieldOutlineProps } from "../focus";
import { BaseDropdown, ClickAwayOpenState } from '../Popover/Popover';
import SearchIcon from '../Icons/SearchIcon';
import MenuList, { Item, ItemOuter } from '../MenuList';
import { canUseDOM } from '../utils/environment';
import CloseFilledThinIcon from '../Icons/CloseFilledThinIcon';
import Link from '../Link';
import { SCHEME_LIGHT, SCHEME_DARK } from '../theme';
var BaseInput = styled(Box).withConfig({
  displayName: "Input__BaseInput",
  componentId: "sc-1m4zhge-0"
})({
  ':not([type="checkbox"]):not([type="radio"])': {
    appearance: 'none'
  }
}, focusOutline, focusBoxShadow, focusBorder);
export var isDisabled = function isDisabled(props) {
  return props.variant === 'disabled' || props.disabled;
};
export var isError = function isError(props) {
  return props.variant === 'error' && !props.disabled;
};
export var formFieldStyles = function formFieldStyles(props) {
  return _objectSpread({
    as: 'input',
    display: 'inline',
    width: props.theme.formField.width,
    height: props.theme.formField.height,
    lineHeight: props.theme.formField.height,
    textStyle: props.theme.formField.textStyle,
    borderRadius: props.theme.radius,
    borderColor: isError(props) ? props.theme.colors.badInputBdr : props.theme.colors.keyline,
    bg: isError(props) ? props.theme.colors.errorBg : isDisabled(props) ? props.theme.colors.bgPlaceholder : props.theme.colors.bg,
    borderWidth: props.theme.formField.borderWidth,
    borderStyle: 'solid',
    pl: props.theme.formField.paddingHorizontal,
    pr: props.theme.formField.paddingHorizontal,
    color: isDisabled(props) ? props.theme.colors.labelPlaceholder : props.theme.colors.label,
    WebkitTextFillColor: isDisabled(props) ? props.theme.colors.labelPlaceholder : undefined,
    opacity: isDisabled(props) ? 1 : undefined
  }, focusFieldOutlineProps(props));
};
var BaseTextInput = useThemeDefaults(formFieldStyles, BaseInput);

var isEmpty = function isEmpty(str) {
  return !str || 0 === str.length;
};

var basicInputIconStyles = function basicInputIconStyles() {
  return {
    color: "labelCaption",
    width: "14px",
    height: "14px",
    position: "absolute",
    top: "50%",
    mt: "-7px"
  };
};

export var WithIconWrapper = styled(Box).withConfig({
  displayName: "Input__WithIconWrapper",
  componentId: "sc-1m4zhge-1"
})(["display:inline-block;position:relative;"]);

var WithIcon = function WithIcon(_ref2) {
  var startIcon = _ref2.startIcon,
      startIconSize = _ref2.startIconSize,
      endIcon = _ref2.endIcon,
      endIconSize = _ref2.endIconSize,
      boxProps = _ref2.boxProps,
      children = _ref2.children;

  if (startIcon && !startIconSize || endIcon && !endIconSize) {
    throw new Error('You have to specify size of passed icons');
  }

  if (!children) {
    throw new Error('You have to pass children');
  }

  var iconStyles = _objectSpread(_objectSpread({}, startIcon ? {
    pl: startIconSize * 2
  } : {}), endIcon ? {
    pr: endIconSize * 2
  } : {});

  return /*#__PURE__*/React.createElement(WithIconWrapper, boxProps, startIcon && /*#__PURE__*/React.cloneElement(startIcon, {
    width: startIconSize,
    height: startIconSize
  }), /*#__PURE__*/React.cloneElement(children, iconStyles), endIcon && /*#__PURE__*/React.cloneElement(endIcon, {
    width: endIconSize,
    height: endIconSize
  }));
};

var isNil = function isNil(variable) {
  return typeof variable === 'undefined' || variable === null;
};

var _ref4 = /*#__PURE__*/React.createElement(CloseFilledThinIcon, null);

export var Text = withTheme( /*#__PURE__*/React.forwardRef(function (_ref3, ref) {
  var IconComponent = _ref3.IconComponent,
      clearable = _ref3.clearable,
      onChange = _ref3.onChange,
      boxProps = _ref3.boxProps,
      propWidth = _ref3.width,
      theme = _ref3.theme,
      _ref3$clearButtonProp = _ref3.clearButtonProps,
      clearButtonProps = _ref3$clearButtonProp === void 0 ? {} : _ref3$clearButtonProp,
      props = _objectWithoutProperties(_ref3, _excluded);

  var width = isNil(propWidth) ? theme.formField.width : propWidth;

  var _ref = useRef(null);

  var inputRef = ref ? ref : _ref;

  var _useState = useState(!isEmpty(props.value)),
      _useState2 = _slicedToArray(_useState, 2),
      clearIconIsVisible = _useState2[0],
      setVisibilityClearIcon = _useState2[1];

  var onClear = useCallback(function () {
    if (canUseDOM()) {
      if (inputRef.current) {
        var input = inputRef.current;
        var inputPrototype = window.HTMLInputElement.prototype;
        var nativeSetter = Object.getOwnPropertyDescriptor(inputPrototype, 'value').set;
        nativeSetter.call(input, '');
        input.dispatchEvent(new Event('input', {
          bubbles: true
        }));
      }
    }
  }, [inputRef]);
  var onChangeHandler = useCallback(function (event) {
    setVisibilityClearIcon(!isEmpty(event.target.value));

    if (isFunction(onChange)) {
      if (isFunction(event.persist)) {
        // this is needed to make possible handle event in async way
        event.persist();
      }

      onChange(event);
    }
  }, [onChange]);
  return /*#__PURE__*/React.createElement(WithIcon, {
    startIcon: IconComponent && /*#__PURE__*/React.createElement(IconComponent, _extends({}, basicInputIconStyles(), {
      left: "8px",
      mt: "-7px"
    })),
    startIconSize: 14,
    endIcon: clearIconIsVisible && clearable && /*#__PURE__*/React.createElement(Link, _extends({
      as: "button"
    }, basicInputIconStyles(), {
      width: "15px",
      height: "15px",
      onClick: onClear,
      cursor: "pointer",
      right: "8px"
    }, clearButtonProps), _ref4),
    endIconSize: 15,
    boxProps: _objectSpread(_objectSpread({}, boxProps), {}, {
      width: width
    })
  }, /*#__PURE__*/React.createElement(BaseTextInput, _extends({
    ref: inputRef,
    onChange: onChangeHandler
  }, props)));
}));
process.env.NODE_ENV !== "production" ? Text.propTypes = {
  IconComponent: PropTypes.any,
  clearable: PropTypes.bool
} : void 0;
export var TextArea = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Text, _extends({
    as: "textarea",
    ref: ref
  }, props));
}); // ssr-compatible btoa

var _btoa = function _btoa(text) {
  return typeof btoa === "function" ? btoa(text) : Buffer.from(text).toString("base64");
}; // TODO: can this be created on postinstall from @tidbits/icons?


var arrowSvg = function arrowSvg(_ref5) {
  var fill = _ref5.fill,
      size = _ref5.size;
  return "\n<svg xmlns=\"http://www.w3.org/2000/svg\" width=\"".concat(size, "\" height=\"").concat(size, "\" viewBox=\"0 0 290 290\">\n  <path fill-rule=\"evenodd\" fill=\"").concat(fill, "\" d=\"M10.3164559,97.7017728 L131.8073,219.192686 C135.54852,222.933892 140.274246,224.706044 144.99993,224.706044 C149.725782,224.706044 154.648381,222.933892 158.19263,219.192686 L279.683487,97.7017728 C283.227875,94.1575237 285,89.4318118 285,84.5092127 C285,74.6638749 277.517588,66 266.490787,66 C261.765075,66 256.842476,67.7721245 253.298088,71.3165131 L144.99993,179.614629 L36.7018286,71.3165131 C33.1575237,67.7721245 28.4317839,66 23.5091429,66 C13.0731365,66 5,74.0730737 5,84.5092127 C5,89.4318118 6.77215243,94.1575237 10.3164559,97.7017728 Z\"/>\n</svg>\n");
}; // 24-checkmark-filled.svg, same TODO as above


var checkmarkSvg = function checkmarkSvg(_ref6) {
  var fill = _ref6.fill,
      size = _ref6.size;
  return "\n  <svg xmlns=\"http://www.w3.org/2000/svg\" width=\"".concat(size, "\" height=\"").concat(size, "\" viewBox=\"0 0 290 290\">\n    <path fill=\"").concat(fill, "\" fill-rule=\"evenodd\" d=\"M145.000061,285 C222.017092,285 285,222.000006 285,145 C285,67.999994 222.017092,5 145.000061,5 C67.9828958,5 5,67.999994 5,145 C5,222.000006 67.9828958,285 145.000061,285 Z M127.200525,212.268296 C122.408326,212.268296 119.32764,210.560982 116.931559,208.000006 L73.2885178,160.024427 C71.0635819,157.804882 69.8655292,154.561043 69.8655292,151.317083 C69.8655292,143.80487 75.5134537,138.341486 83.5574715,138.341486 C87.8361982,138.341486 90.9168843,139.707332 93.8264131,142.780455 L126.515871,178.46342 L191.381485,87.2926744 C194.804389,82.5121529 197.885148,80.8048757 203.361891,80.8048757 C211.063606,80.8048757 216.540349,86.4390962 216.540349,93.7804731 C216.540349,96.512165 215.513429,99.5854092 213.801978,102.146386 L137.982878,207.317083 C135.586772,210.390255 131.992735,212.268296 127.200525,212.268296 Z\"/>\n  </svg>\n");
};

var selectArrow = function selectArrow(_ref7) {
  var theme = _ref7.theme;
  return {
    backgroundImage: "url(data:image/svg+xml;base64,".concat(_btoa(arrowSvg({
      fill: theme.colors[theme.formField.selectArrowColor],
      size: theme.formField.selectArrowSize
    })), ")"),
    backgroundRepeat: 'no-repeat',
    backgroundPosition: "center right ".concat(theme.formField.selectArrowOffset),
    paddingRight: theme.formField.selectPaddingRight
  };
};

export var SelectBase = /*#__PURE__*/React.forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Text, _extends({
    as: "select",
    ref: ref
  }, props));
});
export var Select = styled(SelectBase).withConfig({
  displayName: "Input__Select",
  componentId: "sc-1m4zhge-2"
})(selectArrow);

var baseCheckboxProps = function baseCheckboxProps(props) {
  return _objectSpread({
    as: "input",
    type: "checkbox",
    mr: props.theme.inlineFormLabelPadding,
    ml: "0px",
    mt: "0px",
    mb: "0px"
  }, focusFieldOutlineProps(props));
};

export var Checkbox = useThemeDefaults(function (props) {
  return _objectSpread(_objectSpread({}, baseCheckboxProps(props)), {}, {
    verticalAlign: "middle",
    position: "relative",
    top: "-1.5px"
  });
}, BaseInput);
export var CircleCheckbox = useThemeDefaults(function (props) {
  return _objectSpread(_objectSpread({}, baseCheckboxProps(props)), {}, {
    verticalAlign: 'text-top',
    position: "relative",
    height: "5px",
    width: props.theme.formField.circleCheckboxSize
  });
}, styled(Box).withConfig({
  displayName: "Input__CircleCheckbox",
  componentId: "sc-1m4zhge-3"
})(function (_ref8) {
  var theme = _ref8.theme,
      color = _ref8.color,
      size = _ref8.size,
      zIndex = _ref8.zIndex,
      props = _objectWithoutProperties(_ref8, _excluded2);

  return {
    appearance: "none",
    ":before": {
      content: '""',
      top: '0',
      borderRadius: "50%",
      position: "absolute",
      border: "1px solid",
      height: size || theme.formField.circleCheckboxSize,
      borderColor: theme.colors.keyline,
      width: size || theme.formField.circleCheckboxSize,
      backgroundColor: theme.colors.bg,
      zIndex: zIndex || "auto"
    },
    ":checked": {
      ":before": {
        borderColor: "transparent",
        backgroundImage: "url(data:image/svg+xml;base64,".concat(_btoa(checkmarkSvg({
          fill: theme.colors[color] || color || theme.colors.ctrl,
          size: size || theme.formField.circleCheckboxSize
        })), ")"),
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "".concat(size || theme.formField.circleCheckboxSize, " ").concat(size || theme.formField.circleCheckboxSize)
      }
    },
    ":focus": {
      outline: "none"
    },
    ":focus-visible": {
      ":before": {
        outline: "".concat(theme.colors.focus, " solid 3px")
      }
    },
    ":focus:not(:focus-visible)": {
      outline: "none"
    },
    ":disabled": {
      ":before": {
        backgroundColor: theme.colors.footerBg
      },
      ":checked": {
        ":before": {
          opacity: theme.formField.circleCheckboxDisabledOpacity
        }
      }
    }
  };
}));
export var Radio = useThemeDefaults(function (props) {
  return _objectSpread({
    as: 'input',
    type: 'radio',
    mr: props.theme.inlineFormLabelPadding,
    ml: '0px',
    mt: '0px',
    mb: '0px',
    verticalAlign: 'middle',
    position: 'relative',
    top: '-1.5px'
  }, focusFieldOutlineProps(props));
}, BaseInput);

var pickCursor = function pickCursor(props) {
  return props.disabled ? 'default' : 'pointer';
};

var InnerFile = styled('input').attrs({
  type: 'file'
}).withConfig({
  displayName: "Input__InnerFile",
  componentId: "sc-1m4zhge-4"
})(["opacity:0;z-index:2;position:absolute;width:100%;height:100%;top:0;left:0;cursor:", ";"], pickCursor);
var OuterFile = useThemeDefaults(function (props) {
  return _objectSpread(_objectSpread({}, formFieldStyles(props)), {}, {
    position: 'relative',
    color: isDisabled(props) ? props.theme.colors.labelPlaceholder : props.theme.colors.ctrl
  });
}, styled(T).attrs(function (props) {
  return {
    style: props.focused ? focusFieldOutline(props) : {
      outline: 'none'
    }
  };
}).withConfig({
  displayName: "Input__OuterFile",
  componentId: "sc-1m4zhge-5"
})([""]));

var UnstyledFile = /*#__PURE__*/function (_React$Component) {
  _inherits(UnstyledFile, _React$Component);

  var _super = _createSuper(UnstyledFile);

  function UnstyledFile(props) {
    var _this;

    _classCallCheck(this, UnstyledFile);

    _this = _super.call(this, props);
    _this.inputRef = /*#__PURE__*/React.createRef();
    _this.state = {
      files: [],
      focused: false
    };
    return _this;
  }

  _createClass(UnstyledFile, [{
    key: "render",
    value: function render() {
      var _this2 = this;

      var _this$props = this.props,
          children = _this$props.children,
          accept = _this$props.accept,
          capture = _this$props.capture,
          files = _this$props.files,
          multiple = _this$props.multiple,
          required = _this$props.required,
          value = _this$props.value,
          disabled = _this$props.disabled,
          inputProps = _this$props.inputProps,
          _this$props$onChange = _this$props.onChange,
          _onChange = _this$props$onChange === void 0 ? function () {} : _this$props$onChange,
          props = _objectWithoutProperties(_this$props, _excluded3);

      var innerProps = {
        accept: accept,
        capture: capture,
        disabled: disabled,
        files: files,
        multiple: multiple,
        required: required,
        value: value,
        'aria-label': props['aria-label']
      };
      delete props['aria-label'];
      return /*#__PURE__*/React.createElement(OuterFile, _extends({
        as: "div",
        display: "block",
        textAlign: "left",
        disabled: disabled,
        focused: this.state.focused
      }, props), /*#__PURE__*/React.createElement(InnerFile, _extends({}, innerProps, inputProps, {
        ref: this.inputRef,
        onChange: function onChange(e) {
          _onChange(e);

          _this2.setState(function () {
            return {
              files: _this2.inputRef.current ? Array.from(_this2.inputRef.current.files) : []
            };
          });
        },
        onFocus: function onFocus() {
          _this2.setState(function () {
            return {
              focused: true
            };
          });
        },
        onBlur: function onBlur() {
          _this2.setState(function () {
            return {
              focused: false
            };
          });
        }
      })), children({
        files: this.state.files,
        ref: this.inputRef
      }));
    }
  }]);

  return UnstyledFile;
}(React.Component);

export var File = styled(UnstyledFile).withConfig({
  displayName: "Input__File",
  componentId: "sc-1m4zhge-6"
})(["cursor:", ";", ""], pickCursor, truncate);
export var SelectButton = useThemeDefaults(function (props) {
  return _objectSpread(_objectSpread({}, formFieldStyles(props)), {}, {
    display: 'block',
    as: 'button',
    role: 'button'
  }, focusFieldOutlineProps(props));
}, styled(T).withConfig({
  displayName: "Input__SelectButton",
  componentId: "sc-1m4zhge-7"
})(selectArrow, truncate, focusBorder, focusBoxShadow, focusOutline));
var DEFAULT_PLACEHOLDER = 'Search';
var DropdownSelectFooter = withTheme(function (_ref9) {
  var children = _ref9.children,
      variant = _ref9.variant,
      theme = _ref9.theme;
  return /*#__PURE__*/React.createElement(Box, {
    py: variant === 'sticky' ? '20px' : '15px',
    textStyle: "bodyRegular",
    textAlign: "center",
    borderTop: variant === 'sticky' ? "1px solid ".concat(theme.colors.keyline) : 'none'
  }, children);
});
export var DropdownSelectMenu = withTheme(function (_ref10) {
  var theme = _ref10.theme,
      typeahead = _ref10.typeahead,
      typeaheadInputRef = _ref10.typeaheadInputRef,
      _ref10$menuListProps = _ref10.menuListProps,
      menuListProps = _ref10$menuListProps === void 0 ? {} : _ref10$menuListProps,
      _ref10$FooterLink = _ref10.FooterLink,
      FooterLink = _ref10$FooterLink === void 0 ? null : _ref10$FooterLink,
      _ref10$footerLinkVari = _ref10.footerLinkVariant,
      footerLinkVariant = _ref10$footerLinkVari === void 0 ? 'sticky' : _ref10$footerLinkVari,
      noResultsLabel = _ref10.noResultsLabel,
      selected = _ref10.selected,
      clearable = _ref10.clearable,
      placeholder = _ref10.placeholder,
      filterEnabled = _ref10.filterEnabled,
      children = _ref10.children,
      optionRenderer = _ref10.optionRenderer,
      _ref10$onFilterChange = _ref10.onFilterChange,
      onFilterChange = _ref10$onFilterChange === void 0 ? function () {} : _ref10$onFilterChange,
      _ref10$onChange = _ref10.onChange,
      onChange = _ref10$onChange === void 0 ? function () {} : _ref10$onChange,
      _ref10$hide = _ref10.hide,
      hide = _ref10$hide === void 0 ? function () {} : _ref10$hide;

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      stateFilter = _useState4[0],
      setStateFilter = _useState4[1];

  var setFilterCallback = useCallback(function (filter) {
    setStateFilter(filter);

    if (isFunction(onFilterChange)) {
      onFilterChange(filter);
    }
  }, [onFilterChange, setStateFilter]);
  var resetFilterCallback = useCallback(function () {
    setStateFilter('');
  }, [setStateFilter]);
  var optionOnClickCallback = useCallback(function (value) {
    resetFilterCallback();
    onChange(value);
    hide();
  }, [hide, onChange, resetFilterCallback]);
  var filteredChildren = useMemo(function () {
    var childArray = React.Children.toArray(children);
    return stateFilter === '' || filterEnabled === false ? childArray : childArray.filter(function (child) {
      return child.props.children.toString().toLowerCase().indexOf(stateFilter.toLowerCase()) !== -1;
    });
  }, [children, stateFilter, filterEnabled]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, typeahead && /*#__PURE__*/React.createElement(Box, {
    width: "100%",
    position: "relative",
    minWidth: "300px",
    pl: "12px",
    pr: "20px",
    pb: "15px",
    mb: "15px",
    pt: "10px",
    borderBottom: "1px solid ".concat(theme.colors.keyline)
  }, /*#__PURE__*/React.createElement(Text, {
    type: "text",
    placeholder: placeholder,
    borderStyle: "none",
    clearable: clearable,
    IconComponent: SearchIcon,
    onChange: function onChange(e) {
      setFilterCallback(e.target.value);
    },
    value: stateFilter || '',
    ref: typeaheadInputRef
  })), /*#__PURE__*/React.createElement(MenuList, _extends({
    role: "menu"
  }, menuListProps, typeahead ? {
    pt: 0
  } : {}), !filteredChildren.length ? /*#__PURE__*/React.createElement(T, {
    color: "labelPlaceholder",
    px: theme.menuList.paddingX,
    textAlign: "center",
    my: "0"
  }, noResultsLabel) : filteredChildren.map(function (child) {
    var optionDisabled = child.props.disabled;
    var optionValue = child.props.value;
    var optionChildren = child.props.children;
    return isFunction(optionRenderer) ? /*#__PURE__*/React.createElement(React.Fragment, {
      key: optionValue
    }, optionRenderer(_objectSpread(_objectSpread({}, child.props), {}, {
      disabled: optionDisabled,
      children: optionChildren,
      value: optionValue,
      selected: optionValue === selected,
      onClick: function onClick() {
        optionOnClickCallback(optionValue);
      }
    }))) : /*#__PURE__*/React.createElement(Item, {
      role: "button",
      type: "button",
      key: optionValue,
      selected: optionValue === selected,
      disabled: optionDisabled,
      width: "100%",
      onClick: function onClick() {
        optionOnClickCallback(optionValue);
      }
    }, optionChildren);
  }), FooterLink && footerLinkVariant === 'inline' ? /*#__PURE__*/React.createElement(ItemOuter, {
    as: "li"
  }, /*#__PURE__*/React.createElement(DropdownSelectFooter, {
    variant: footerLinkVariant
  }, FooterLink)) : null), FooterLink && footerLinkVariant === 'sticky' ? /*#__PURE__*/React.createElement(DropdownSelectFooter, {
    variant: footerLinkVariant
  }, FooterLink) : null);
});
process.env.NODE_ENV !== "production" ? DropdownSelectMenu.propTypes = {
  /**
   * External function to filter elements. Can be used for async calls
   * or for custom filtering (i.e. fuse.js)
   */
  onFilterChange: PropTypes.func,

  /**
   * Whether component will filter children or not
   */
  filterEnabled: PropTypes.bool,
  footerLinkVariant: PropTypes.oneOf(['sticky', 'inline'])
} : void 0;
DropdownSelectMenu.defaultProps = {
  filterEnabled: true
};
var DEFAULT_MENU_LIST_PROPS = {
  maxHeight: '45vh'
};

var getSelectedOption = function getSelectedOption(value, options) {
  if (!options.length) {
    return undefined;
  }

  return options.find(function (option) {
    return option.props.value === value;
  });
};

export var DropdownSelect = /*#__PURE__*/React.memo(function (_ref11) {
  var value = _ref11.value,
      children = _ref11.children,
      _ref11$clearable = _ref11.clearable,
      clearable = _ref11$clearable === void 0 ? true : _ref11$clearable,
      _ref11$selectedValueP = _ref11.selectedValuePlaceholder,
      selectedValuePlaceholder = _ref11$selectedValueP === void 0 ? '-' : _ref11$selectedValueP,
      _ref11$placeholder = _ref11.placeholder,
      placeholder = _ref11$placeholder === void 0 ? DEFAULT_PLACEHOLDER : _ref11$placeholder,
      optionRenderer = _ref11.optionRenderer,
      _ref11$onChange = _ref11.onChange,
      onChange = _ref11$onChange === void 0 ? function () {} : _ref11$onChange,
      props = _objectWithoutProperties(_ref11, _excluded4);

  var options = useMemo(function () {
    return React.Children.toArray(children);
  }, [children]);

  var getDisplayValue = function getDisplayValue(selected) {
    return selected ? selected.props.children : selectedValuePlaceholder;
  };

  var _useState5 = useState(null),
      _useState6 = _slicedToArray(_useState5, 2),
      selected = _useState6[0],
      setSelected = _useState6[1];

  var getSelectedValue = function getSelectedValue(selected) {
    return selected ? selected.props.value : null;
  };

  useEffect(function () {
    setSelected(getSelectedOption(value, options));
  }, [value, options]);
  var onChangeMenu = useCallback(function (val) {
    setSelected(getSelectedOption(val, options));
    onChange(val);
  }, [onChange, options]);

  var name = props.name,
      id = props.id,
      dropdownProps = props.dropdownProps,
      _props$noResultsLabel = props.noResultsLabel,
      noResultsLabel = _props$noResultsLabel === void 0 ? "No Results" : _props$noResultsLabel,
      _props$menuListProps = props.menuListProps,
      menuListProps = _props$menuListProps === void 0 ? {} : _props$menuListProps,
      typeahead = props.typeahead,
      onFilterChange = props.onFilterChange,
      filterEnabled = props.filterEnabled,
      _props$FooterLink = props.FooterLink,
      FooterLink = _props$FooterLink === void 0 ? null : _props$FooterLink,
      footerLinkVariant = props.footerLinkVariant,
      buttonProps = _objectWithoutProperties(props, _excluded5);

  var menuProps = useMemo(function () {
    return _objectSpread(_objectSpread({}, DEFAULT_MENU_LIST_PROPS), menuListProps);
  }, [menuListProps]);
  var typeaheadInputRef = useRef(null);
  var focusTypeaheadCallback = useCallback(function () {
    if (typeahead && typeaheadInputRef !== null && typeaheadInputRef !== void 0 && typeaheadInputRef.current) {
      setTimeout(function () {
        return typeaheadInputRef === null || typeaheadInputRef === void 0 ? void 0 : typeaheadInputRef.current.focus({
          preventScroll: true
        });
      }, 0);
    }
  });
  return /*#__PURE__*/React.createElement(ClickAwayOpenState, null, function (_ref12) {
    var isHidden = _ref12.isHidden,
        hide = _ref12.hide,
        setHidden = _ref12.setHidden,
        containerRef = _ref12.containerRef,
        targetRef = _ref12.targetRef;
    return /*#__PURE__*/React.createElement(BaseDropdown, _extends({
      ref: containerRef,
      hidden: isHidden,
      role: "tooltip",
      placement: "bottom",
      onOpen: focusTypeaheadCallback,
      target: function target(_ref13) {
        var ref = _ref13.ref;
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(SelectButton, _extends({
          id: id,
          ref: mergeRefs(ref, targetRef),
          onClick: function onClick() {
            if (!isDisabled(props)) {
              setHidden(!isHidden);
            }
          },
          truncate: true
        }, buttonProps), getDisplayValue(selected)), /*#__PURE__*/React.createElement("input", {
          type: "hidden",
          onChange: function onChange() {},
          value: getSelectedValue(selected) || '',
          name: name
        }));
      }
    }, dropdownProps), /*#__PURE__*/React.createElement(DropdownSelectMenu, {
      optionRenderer: optionRenderer,
      children: children,
      hide: hide,
      onChange: onChangeMenu,
      typeahead: typeahead,
      typeaheadInputRef: typeaheadInputRef,
      selected: getSelectedValue(selected),
      menuListProps: menuProps,
      placeholder: placeholder,
      clearable: clearable,
      noResultsLabel: noResultsLabel,
      onFilterChange: onFilterChange,
      filterEnabled: filterEnabled,
      FooterLink: FooterLink,
      footerLinkVariant: footerLinkVariant
    }));
  });
});
export var RangeSlider = useThemeDefaults(function (props) {
  return _objectSpread({
    as: 'input',
    type: 'range',
    min: 1,
    max: 100,
    // styling
    width: '100%',
    marginTop: '9px',
    marginBottom: '18px'
  }, focusFieldOutlineProps(props));
}, styled(BaseInput).withConfig({
  displayName: "Input__RangeSlider",
  componentId: "sc-1m4zhge-8"
})(function (_ref14) {
  var _SCHEME_LIGHT$SCHEME_;

  var theme = _ref14.theme,
      width = _ref14.width,
      marginTop = _ref14.marginTop,
      marginBottom = _ref14.marginBottom;
  var THUMB_SIZE = 18;
  /*
   * Due to the range slider thumb using different color keys for each scheme,
   * we have to toggle the keys based on the current theme.
   * The long term goal is to move these into the official color API rather than bodging it.
   */

  var rangeThumbColors = (_SCHEME_LIGHT$SCHEME_ = {}, _defineProperty(_SCHEME_LIGHT$SCHEME_, SCHEME_LIGHT, {
    thumb: theme.colors.bg,
    border: theme.colors.keyline
  }), _defineProperty(_SCHEME_LIGHT$SCHEME_, SCHEME_DARK, {
    thumb: theme.colors.labelCaption,
    border: theme.colors.bg
  }), _SCHEME_LIGHT$SCHEME_)[theme.colors.SCHEME];
  var stylesSharedTrack = {
    width: '100%',
    height: '3px',
    cursor: 'pointer',
    borderRadius: "".concat(theme.radii.small, "px"),
    background: theme.colors.keyline
  };
  var stylesSharedThumb = {
    position: 'relative',
    appearance: 'none',
    boxShadow: "0 1px 4px ".concat(theme.colors.keyline),
    height: "".concat(THUMB_SIZE, "px"),
    width: "".concat(THUMB_SIZE, "px"),
    borderRadius: "".concat(THUMB_SIZE, "px"),
    background: rangeThumbColors.thumb,
    cursor: 'pointer',
    border: "1px solid ".concat(rangeThumbColors.border)
  };
  return {
    border: 0,
    marginTop: marginTop,
    marginBottom: marginBottom,
    width: width,
    backgroundColor: 'transparent',
    height: '20px',
    // only impacts the amount of space the component takes up
    borderRadius: "".concat(theme.radii.small, "px"),
    '::-webkit-slider-runnable-track': stylesSharedTrack,
    '::-webkit-slider-thumb': _objectSpread(_objectSpread({}, stylesSharedThumb), {}, {
      height: THUMB_SIZE + 2,
      width: THUMB_SIZE + 2,
      marginTop: '-8px'
    }),
    '::-moz-range-track': stylesSharedTrack,
    '::-moz-range-thumb': stylesSharedThumb
  };
}));
var ToggleSwitchSlider = styled(Box).attrs({
  as: "span"
}).withConfig({
  displayName: "Input__ToggleSwitchSlider",
  componentId: "sc-1m4zhge-9"
})([""]);

var _ref16 = /*#__PURE__*/React.createElement(ToggleSwitchSlider, null);

export var ToggleSwitch = styled( /*#__PURE__*/React.forwardRef(function (_ref15, ref) {
  var width = _ref15.width,
      height = _ref15.height,
      sliderSpacing = _ref15.sliderSpacing,
      transitionDuration = _ref15.transitionDuration,
      containerProps = _ref15.containerProps,
      className = _ref15.className,
      props = _objectWithoutProperties(_ref15, _excluded6);

  return /*#__PURE__*/React.createElement(Box, _extends({
    className: className
  }, containerProps), /*#__PURE__*/React.createElement("input", _extends({
    type: "checkbox",
    "aria-hidden": true,
    ref: ref
  }, props)), _ref16);
})).withConfig({
  displayName: "Input__ToggleSwitch",
  componentId: "sc-1m4zhge-10"
})(["--ts-width:", ";--ts-height:", ";--ts-slider-spacing:", ";--ts-slider-size:calc(var(--ts-height) - var(--ts-slider-spacing) * 2);--ts-transition:all ", " ease;position:relative;display:inline-block;width:var(--ts-width);height:var(--ts-height);input{opacity:0;width:100%;height:100%;margin:0;padding:0;cursor:pointer;}", "{position:absolute;top:0;left:0;right:0;bottom:0;pointer-events:none;background-color:", ";border-radius:var(--ts-width);transition:var(--ts-transition);}", ":after{position:absolute;content:\"\";height:var(--ts-slider-size);width:var(--ts-slider-size);top:var(--ts-slider-spacing);left:var(--ts-slider-spacing);background-color:", ";border-radius:50%;box-shadow:0 1px 2px 0 rgba(0,0,0,0.25);transition:var(--ts-transition);}", ":before{position:absolute;width:50%;height:100%;display:flex;align-items:center;justify-content:center;font-size:calc(var(--ts-height) / 2);}input:checked + ", ":before{left:0;color:", ";content:\"\u23FD\";}input:not(:checked) + ", ":before{left:50%;color:", ";content:\"\u2B58\";}input:checked + ", ":after{transform:translateX(calc(var(--ts-width) / 2 - var(--ts-slider-spacing)));}input:checked + ", "{background-color:", ";}input:focus + ", "{box-shadow:0 0 0 3px ", ";}input:disabled{cursor:default;}input:disabled + ", "{opacity:0.5;}"], function (_ref17) {
  var width = _ref17.width;
  return width;
}, function (_ref18) {
  var height = _ref18.height;
  return height;
}, function (_ref19) {
  var sliderSpacing = _ref19.sliderSpacing;
  return sliderSpacing;
}, function (_ref20) {
  var transitionDuration = _ref20.transitionDuration;
  return transitionDuration;
}, ToggleSwitchSlider, function (_ref21) {
  var theme = _ref21.theme;
  return theme.colors.keyline;
}, ToggleSwitchSlider, function (_ref22) {
  var theme = _ref22.theme;
  return theme.colors.bg;
}, ToggleSwitchSlider, ToggleSwitchSlider, function (_ref23) {
  var theme = _ref23.theme;
  return theme.colors.bg;
}, ToggleSwitchSlider, function (_ref24) {
  var theme = _ref24.theme;
  return theme.colors.navLabelDisabled;
}, ToggleSwitchSlider, ToggleSwitchSlider, function (_ref25) {
  var theme = _ref25.theme;
  return theme.colors.ctrl;
}, ToggleSwitchSlider, function (_ref26) {
  var theme = _ref26.theme;
  return theme.colors.focus;
}, ToggleSwitchSlider);
ToggleSwitch.defaultProps = {
  width: "31px",
  height: "18px",
  sliderSpacing: "2px",
  transitionDuration: "250ms",
  containerProps: {}
};